import { useEffect } from 'react'
import styled from 'styled-components'

import useDispatch from 'hooks/useDispatch'
import useReduxSelector from 'hooks/useReduxSelector'
import useHasHydrated from 'hooks/useHasHydrated'
import useSearchParams from 'hooks/useSearchParams'

import Button from '../../../components/display/Button'
import InstantBookBadge from '../../../components/display/InstantBookBadge'
import { slugToNice } from '../../../components/utils/stringUtils'
import { formatPrice } from '../../../components/utils/pricing'

import { resetEnquiryPrices } from '../../../actions/enquiryActions'

export const MobileActionBarWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  color: ${({ theme }) => theme.brand.forest};
  background-color: ${({ theme }) => theme.brand.sand};
  z-index: 8;
`

const PriceWrapper = styled.div`
  flex: 1;
`

type PriceProps = {
  $instantBookEnabled?: boolean
}

const Price = styled.div<PriceProps>`
  display: flex;
  flex-direction: row;
  align-items: baseline;

  strong {
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-lg']?.['font-size']?.value};
    font-weight: 500;
    line-height: 1.4;
    color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  }

  small {
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-xxs']?.['font-size']?.value};
    color: ${({ theme }) => theme?.['-ds-neutral-500']?.value};
    margin: 0 4px;
    font-weight: 400;
    line-height: 1.4;
  }
`

const IconButton = styled(Button)`
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  .icon {
    font-size: 18px;
    margin: 0;
  }
`

const Actions = styled.div`
  display: flex;

  ${Button} {
    padding: 12px 16px;
    border: 1px solid ${({ theme }) => theme.brand.jungle};
  }

  ${Button}, ${IconButton} {
    min-width: 52px;
    margin-right: 8px;

    &:last-child {
      margin-right: 0;
    }

    svg {
      width: 18px;
      margin: 0;
    }
  }
`

type MobileActionsBarProps = {
  listing: {
    id: number
    instant_book_enabled: boolean
    price: string
    pricing_suffix: string
    giftable: boolean
  }
  onShowBookingForm: () => any
}

const MobileActionBar = ({
  listing,
  onShowBookingForm,
}: MobileActionsBarProps) => {
  const dispatch = useDispatch()

  const searchParams = useSearchParams()

  const hasHydrated = useHasHydrated()

  const hasDatesInQueryString =
    hasHydrated &&
    searchParams?.has('startDate') &&
    searchParams?.has('endDate')

  const enquiry = useReduxSelector((state) => state.enquiry)

  useEffect(() => {
    dispatch(resetEnquiryPrices())
  }, [dispatch])

  const hasTotal = parseFloat(enquiry?.data?.total) > 0

  const hasDatesInEnquiry =
    enquiry?.data?.start_date != null && enquiry?.data?.end_date

  const bookCtaLabel =
    hasDatesInQueryString || hasDatesInEnquiry
      ? 'Book now'
      : 'Check availability'

  return (
    <MobileActionBarWrapper>
      <PriceWrapper>
        <Price $instantBookEnabled={listing.instant_book_enabled}>
          <strong>
            {hasTotal
              ? formatPrice(enquiry.data.total, 0)
              : formatPrice(listing.price, 0)}
          </strong>
          <small>
            {hasTotal ? 'total' : slugToNice(listing.pricing_suffix)}
          </small>
        </Price>
        {listing.instant_book_enabled && (
          <span style={{ marginTop: '8px' }}>
            <InstantBookBadge />
          </span>
        )}
      </PriceWrapper>
      <Actions>
        <Button onClick={onShowBookingForm} uppercase={false}>
          {bookCtaLabel}
        </Button>
      </Actions>
    </MobileActionBarWrapper>
  )
}

export default MobileActionBar
